import { Switch } from '@mantine/core';
import Select from '@xver/components/Atoms/Select/Select';
import { useDisclosure } from '@mantine/hooks';
import type {
    CreateContainerSourceRequest,
    ImagePullPolicy,
} from '@xver/apis/v1/kufer/containers/model';
import InputField from '@xver/components/Atoms/InputField/InputField';
import TagsInput from '@xver/components/Atoms/TagsInput/TagsInput';
import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import CredentialFormModal from '../../ContainerEnginePage/components/CredentialFormModal';
import { useRegistryCredentialsQuery } from '../../ContainerEnginePage/hooks/useRegistryCredentialsQuery';

const CREATE_CREDENTIAL_SENTINEL = '__create_credential__';
const NO_CREDENTIAL = '__no_credential__';

export interface UrlSourceFormValue {
    image: string;
    image_tag: string;
    registry_credential_id: string;
    image_pull_policy: ImagePullPolicy;
    enable_auto_update: boolean;
    mutable_tags: string[];
}

interface UrlSourceFormProps {
    value: UrlSourceFormValue;
    onChange: (value: UrlSourceFormValue) => void;
    error?: string;
    /** False hides the credential picker: kufer accepts a credential only from the project owner. */
    allowCredential?: boolean;
    onImageTouched?: () => void;
}

const SELECT_STYLES = {
    input: {
        backgroundColor: 'var(--surface-2)',
        color: 'var(--fg)',
    },
    option: {
        backgroundColor: 'var(--surface-2)',
        color: 'var(--fg)',
    },
    dropdown: {
        backgroundColor: 'var(--surface-2)',
        borderColor: 'var(--line)',
    },
};

export function toCreateSourceRequest(value: UrlSourceFormValue): CreateContainerSourceRequest {
    return {
        type: 'url',
        image: value.image.trim(),
        image_tag: value.image_tag.trim() || 'latest',
        registry_credential_id: value.registry_credential_id.trim() || undefined,
        image_pull_policy: value.image_pull_policy,
        enable_auto_update: value.enable_auto_update,
        mutable_tags: value.enable_auto_update
            ? value.mutable_tags.filter((t) => t.trim().length > 0)
            : undefined,
    };
}

export const DEFAULT_URL_SOURCE_VALUE: UrlSourceFormValue = {
    image: '',
    image_tag: 'latest',
    registry_credential_id: '',
    image_pull_policy: 'IfNotPresent',
    enable_auto_update: false,
    mutable_tags: [],
};

export default function UrlSourceForm({
    value,
    onChange,
    error,
    allowCredential = true,
    onImageTouched,
}: UrlSourceFormProps) {
    const { t } = useTranslation('containerEngine');
    const autoUpdateLabelId = useId();
    const autoUpdateDescriptionId = useId();
    const update = (patch: Partial<UrlSourceFormValue>) => onChange({ ...value, ...patch });

    const { data: credList, isPending: credPending } = useRegistryCredentialsQuery();
    const [createCredOpened, { open: openCreateCred, close: closeCreateCred }] =
        useDisclosure(false);

    const credentialOptions = useMemo(
        () => [
            {
                value: NO_CREDENTIAL,
                label: t('deployment.urlSource.credentialNone', 'None (public image)'),
            },
            ...(credList?.credentials ?? []).map((c) => ({
                value: c.id,
                label: c.endpoint ? `${c.name} — ${c.endpoint}` : c.name,
            })),
            {
                value: CREATE_CREDENTIAL_SENTINEL,
                label: t('deployment.urlSource.credentialAddNew', '+ Add new credential'),
            },
        ],
        [credList, t]
    );

    return (
        <div className="w-full flex flex-col gap-4">
            <InputField
                size="md"
                label={
                    <h3 className="text-base">
                        {t('deployment.urlSource.image', 'Container image')}
                    </h3>
                }
                placeholder={t(
                    'deployment.urlSource.imagePlaceholder',
                    'nginx or ghcr.io/owner/image'
                )}
                value={value.image}
                onChange={(e) => {
                    update({ image: e.target.value });
                    onImageTouched?.();
                }}
                descriptionBottom={t(
                    'deployment.urlSource.imageDescription',
                    "Full container image reference. Without a registry host, the credential's endpoint decides where it is pulled from."
                )}
                error={error}
                required
            />
            <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                    size="md"
                    label={
                        <h3 className="text-base">{t('deployment.urlSource.tag', 'Image tag')}</h3>
                    }
                    placeholder={t('deployment.urlSource.tagPlaceholder', 'latest')}
                    value={value.image_tag}
                    onChange={(e) => update({ image_tag: e.target.value })}
                    descriptionBottom={t(
                        'deployment.urlSource.tagDescription',
                        "Defaults to 'latest' if left blank."
                    )}
                />
                {allowCredential && (
                    <Select
                        size="md"
                        label={t(
                            'deployment.urlSource.credentialId',
                            'Registry credential (optional, for private images)'
                        )}
                        description={t(
                            'deployment.urlSource.credentialIdDescription',
                            "Only needed for private images. The credential's endpoint decides which registry the image is pulled from."
                        )}
                        inputWrapperOrder={['label', 'input', 'description']}
                        classNames={{
                            label: 'text-sm font-medium text-fg/85',
                            description: 'text-xs text-fg/68',
                        }}
                        value={value.registry_credential_id || NO_CREDENTIAL}
                        onChange={(next) => {
                            if (next === CREATE_CREDENTIAL_SENTINEL) {
                                openCreateCred();
                                return;
                            }
                            update({
                                registry_credential_id: next && next !== NO_CREDENTIAL ? next : '',
                            });
                        }}
                        data={credentialOptions}
                        allowDeselect={false}
                        disabled={credPending}
                        styles={SELECT_STYLES}
                    />
                )}
            </div>

            {allowCredential && (
                <CredentialFormModal
                    mode="create"
                    opened={createCredOpened}
                    onClose={closeCreateCred}
                    onSuccess={(newId) => update({ registry_credential_id: newId })}
                />
            )}
            <Select
                size="md"
                label={t('deployment.urlSource.pullPolicy', 'Image pull policy')}
                description={t(
                    'deployment.urlSource.pullPolicyDescription',
                    'Controls when Knative pulls the image from the registry.'
                )}
                inputWrapperOrder={['label', 'input', 'description']}
                classNames={{
                    label: 'text-sm font-medium text-fg/85',
                    description: 'text-xs text-fg/68',
                }}
                value={value.image_pull_policy}
                onChange={(next) =>
                    update({
                        image_pull_policy: (next ?? 'IfNotPresent') as ImagePullPolicy,
                    })
                }
                data={[
                    {
                        value: 'Always',
                        label: t('deployment.urlSource.pullPolicyAlways', 'Always'),
                    },
                    {
                        value: 'IfNotPresent',
                        label: t('deployment.urlSource.pullPolicyIfNotPresent', 'If not present'),
                    },
                    {
                        value: 'Never',
                        label: t('deployment.urlSource.pullPolicyNever', 'Never'),
                    },
                ]}
                styles={SELECT_STYLES}
            />
            <div className="w-full flex flex-row justify-between items-start gap-4 p-3 rounded-md border border-line bg-surface-2">
                <div className="flex flex-col items-start">
                    <span id={autoUpdateLabelId} className="text-sm font-medium text-fg">
                        {t('deployment.urlSource.autoUpdateTitle', 'Auto-update on tag change')}
                    </span>
                    <span id={autoUpdateDescriptionId} className="text-xs text-fg/68">
                        {t(
                            'deployment.urlSource.autoUpdateDescription',
                            'Redeploy when the image tag is re-pushed (mutable tag tracking).'
                        )}
                    </span>
                </div>
                <Switch
                    aria-labelledby={autoUpdateLabelId}
                    aria-describedby={autoUpdateDescriptionId}
                    size="md"
                    checked={value.enable_auto_update}
                    onChange={(e) => update({ enable_auto_update: e.currentTarget.checked })}
                />
            </div>
            {value.enable_auto_update && (
                <TagsInput
                    size="md"
                    label={
                        <h3 className="text-base">
                            {t('deployment.urlSource.mutableTags', 'Mutable tags')}
                        </h3>
                    }
                    value={value.mutable_tags}
                    onChange={(tags) => update({ mutable_tags: tags })}
                    placeholder={t(
                        'deployment.urlSource.mutableTagsPlaceholder',
                        'latest, stable, ...'
                    )}
                    descriptionBottom={t(
                        'deployment.urlSource.mutableTagsDescription',
                        'Tracked tags that should trigger a redeploy when updated.'
                    )}
                />
            )}
        </div>
    );
}
